/**
 * ContractsSection
 * DataTable con créditos del cliente + modal de detalle al hacer click.
 * El modal muestra: fechas, tabla de amortización, historial de pagos.
 */
import { useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { AmortizationTable } from '@/features/credit/components/AmortizationTable';
import { PaymentHistoryTable } from '@/features/credit/components/PaymentHistoryTable';
import { useBackofficeCredits, useCreditDetail, useCreditPayments } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

interface ContractsSectionProps {
  directoryId: string;
}

// ── helpers ──────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'ACTIVE': return 'success';
    case 'PAID_OFF': case 'SETTLED': return 'info';
    case 'DELINQUENT': return 'warning';
    case 'DEFAULTED': case 'CANCELLED': return 'danger';
    default: return 'default';
  }
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateStr));
  } catch { return dateStr; }
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

// ── columns ─────────────────────────────────────────────────────────────

const creditColumns: ColumnDef<BackofficeCreditsListItem, unknown>[] = [
  {
    accessorKey: 'disbursed_at', header: 'Desembolso',
    cell: ({ getValue }) => formatDateShort(getValue<string>()),
  },
  {
    accessorKey: 'credit_id', header: 'ID',
    cell: ({ getValue }) => <span className="font-mono text-xs" title={getValue<string>()}>{truncateId(getValue<string>())}</span>,
  },
  {
    accessorKey: 'credit_level', header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string>();
      if (!level) return '-';
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? <CreditLevelBadge level={level as CreditLevel} size="sm" /> : <span className="text-xs">{level}</span>;
    },
  },
  {
    accessorKey: 'amount_mxn', header: 'Monto',
    cell: ({ getValue }) => <span className="tabular-nums">{formatCurrency(getValue<number>(), 'MXN', 'es-MX')}</span>,
  },
  {
    accessorKey: 'term_weeks', header: 'Plazo',
    cell: ({ getValue }) => `${getValue<number>()} sem`,
  },
  {
    accessorKey: 'annual_rate', header: 'Tasa Anual',
    cell: ({ getValue }) => <span className="tabular-nums">{(getValue<number>() * 100).toFixed(1)}%</span>,
  },
  {
    accessorKey: 'weekly_payment_mxn', header: 'Pago Semanal',
    cell: ({ getValue }) => <span className="tabular-nums">{formatCurrency(getValue<number>(), 'MXN', 'es-MX')}</span>,
  },
  {
    accessorKey: 'status', header: 'Estado',
    cell: ({ getValue }) => {
      const s = getValue<string>();
      return <Badge variant={getStatusVariant(s)}>{s}</Badge>;
    },
  },
];

// ── main component ──────────────────────────────────────────────────────

export function ContractsSection({ directoryId }: ContractsSectionProps) {
  const { credits, isLoading, isError } = useBackofficeCredits();
  const [selectedCreditId, setSelectedCreditId] = useState<string | null>(null);

  const clientCredits = useMemo(
    () => credits.filter((c) => c.directory_id === directoryId),
    [credits, directoryId],
  );

  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando créditos…</div>;
  if (isError) return <div className="p-4 text-sm text-red-600 dark:text-red-400">Error al cargar créditos.</div>;

  if (clientCredits.length === 0) {
    return <div className="p-4 text-sm text-muted-foreground">Este cliente no tiene créditos.</div>;
  }

  return (
    <div className="p-4">
      <DataTable
        data={clientCredits}
        columns={creditColumns}
        searchPlaceholder="Buscar por ID de crédito..."
        onRowClick={(row) => setSelectedCreditId(row.credit_id)}
        emptyMessage="Sin créditos"
        pageSize={5}
      />

      {selectedCreditId && (
        <CreditDetailModal
          creditId={selectedCreditId}
          directoryId={directoryId}
          onClose={() => setSelectedCreditId(null)}
        />
      )}
    </div>
  );
}

// ── credit detail modal ─────────────────────────────────────────────────

function CreditDetailModal({ creditId, directoryId, onClose }: {
  creditId: string; directoryId: string; onClose: () => void;
}) {
  const { credit, schedule, isLoading, isError } = useCreditDetail(creditId, directoryId);
  const payments = useCreditPayments(creditId);

  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative bg-card border border-border rounded-lg shadow-xl max-w-5xl w-[95vw] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <span className="text-sm font-semibold text-foreground">
            Detalle de Crédito #{creditId.slice(0, 8)}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {isLoading && (
            <div className="py-12 text-center text-sm text-muted-foreground">Cargando detalle del crédito…</div>
          )}

          {isError && (
            <div className="py-12 text-center text-sm text-red-600 dark:text-red-400">Error al cargar detalle.</div>
          )}

          {credit && (
            <>
              {/* Fechas */}
              <div className="rounded-md border border-border bg-muted/30 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Fechas</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <DateItem label="Desembolso" value={credit.disbursed_at} />
                  <DateItem label="Primer Pago" value={credit.first_payment_date} />
                  <DateItem label="Último Pago" value={credit.last_payment_date} />
                  <DateItem label="Creado" value={credit.created_at} />
                </div>
              </div>

              {/* Tabla de amortización */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Tabla de Amortización</p>
                <AmortizationTable schedule={schedule} />
              </div>

              {/* Historial de pagos */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Historial de Pagos</p>
                <PaymentHistoryTable records={payments.records} isLoading={payments.isLoading} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── micro-components ────────────────────────────────────────────────────

function DateItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-muted-foreground">{label}</p>
      <p className="text-sm font-medium tabular-nums text-foreground mt-0.5">{formatDateShort(value)}</p>
    </div>
  );
}
